import { z } from 'zod';

// ---------------------------------------------------------------------------
// Shared shapes: a single quad corner point, and the fixed-order 4-corner
// quad [TL, TR, BR, BL] used both for the auto-detected cutout and for the
// (possibly manually-adjusted) "live" corners persisted on interior_templates.
// idea 2026-07-17-interior-templates-cutout.
// ---------------------------------------------------------------------------

export const pointSchema = z.object({ x: z.number(), y: z.number() });

/** Order is FIXED: [TL, TR, BR, BL] (clockwise from top-left). */
export const quadSchema = z.array(pointSchema).length(4);

export type PointPayload = z.infer<typeof pointSchema>;
export type QuadPayload = z.infer<typeof quadSchema>;

// ---------------------------------------------------------------------------
// interior_template.upload.requested — emitted by admin (emitAndWait) after
// staging a PNG in Storage (bucket `wandero`,
// `staging/interior-templates/{uuid}.png`). Worker downloads, detects the
// transparent cutout as a quad, and persists an interior_templates row.
// ---------------------------------------------------------------------------

export const interiorTemplateUploadRequestedSchema = z.object({
  /** Staging key in the `wandero` bucket, e.g. 'staging/interior-templates/<uuid>.png'. */
  storage_path: z.string().min(1),
  original_filename: z.string().min(1),
  content_type: z.string().min(1),
  size_bytes: z.number().int().positive(),
  name: z.string().min(1).max(200),
  room_category_id: z.number().int().positive().nullable().optional(),
});

export type InteriorTemplateUploadRequestedPayload = z.infer<
  typeof interiorTemplateUploadRequestedSchema
>;

// ---------------------------------------------------------------------------
// interior_template.upload.processed — emitted by worker handler on success
// or error (LOUD-error contract: no-alpha-channel / no-transparent-region /
// region-too-small).
// ---------------------------------------------------------------------------

export const interiorTemplateUploadProcessedSuccessSchema = z.object({
  template_id: z.number().int().positive(),
  storage_path: z.string(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  corners: quadSchema,
  detected_corners: quadSchema,
});

export const interiorTemplateUploadProcessedErrorSchema = z.object({
  error: z.string(),
  storage_path: z.string(),
});

export const interiorTemplateUploadProcessedSchema = z.union([
  interiorTemplateUploadProcessedSuccessSchema,
  interiorTemplateUploadProcessedErrorSchema,
]);

export type InteriorTemplateUploadProcessedPayload = z.infer<
  typeof interiorTemplateUploadProcessedSchema
>;

// ---------------------------------------------------------------------------
// interior_template.composite.requested — emitted by admin (fire-and-forget,
// emitEvent) from the wallpaper page. Worker warps the wallpaper into the
// template quad and composites it under the template PNG, inserting a
// template-based wallpapers_interiors row (source='template'). No PROCESSED
// event — outcome is observed via Realtime on wallpapers_interiors, like
// ai.interior.generate.requested (D6).
// ---------------------------------------------------------------------------

export const interiorTemplateCompositeRequestedSchema = z.object({
  wallpaper_id: z.number().int().positive(),
  template_id: z.number().int().positive(),
  room_category_id: z.number().int().positive(),
});

export type InteriorTemplateCompositeRequestedPayload = z.infer<
  typeof interiorTemplateCompositeRequestedSchema
>;
